package com.speedsoftware.nibbler.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.client.NibblerApiClient
import com.speedsoftware.nibbler.models.ProjectDto
import com.speedsoftware.nibbler.models.UserDto
import com.speedsoftware.nibbler.ui.theme.NibblerColors
import org.jetbrains.compose.resources.decodeToImageBitmap

/** Gives image composables access to the API client's image downloader. */
val LocalApiClient = compositionLocalOf<NibblerApiClient?> { null }

private object ImageCache {
    val bitmaps = HashMap<String, ImageBitmap>()
    val failed = HashSet<String>()
}

/**
 * Loads an image from a URL (which must allow cross-origin reads on web) and shows [fallback]
 * while loading, or when the URL is blank / cannot be loaded or decoded.
 */
@Composable
fun RemoteImage(
    url: String?,
    modifier: Modifier = Modifier,
    contentScale: ContentScale = ContentScale.Crop,
    fallback: @Composable () -> Unit
) {
    val apiClient = LocalApiClient.current
    var bitmap by remember(url) { mutableStateOf(url?.let { ImageCache.bitmaps[it] }) }

    LaunchedEffect(url, apiClient) {
        if (url.isNullOrBlank() || apiClient == null || bitmap != null || url in ImageCache.failed) return@LaunchedEffect
        val decoded = apiClient.fetchImageBytes(url).mapCatching { it.decodeToImageBitmap() }.getOrNull()
        if (decoded != null) {
            ImageCache.bitmaps[url] = decoded
            bitmap = decoded
        } else {
            ImageCache.failed.add(url)
        }
    }

    val loaded = bitmap
    if (loaded != null) {
        Image(bitmap = loaded, contentDescription = null, contentScale = contentScale, modifier = modifier)
    } else {
        Box(modifier = modifier, contentAlignment = Alignment.Center) { fallback() }
    }
}

/** Outline of a person drawn on a canvas; used for tasks nobody is assigned to. */
@Composable
fun UnassignedIcon(size: Dp, modifier: Modifier = Modifier) {
    val color = NibblerColors.DarkOnSurfaceVariant
    Canvas(modifier = modifier.size(size)) {
        val stroke = Stroke(width = 1.2.dp.toPx(), pathEffect = PathEffect.dashPathEffect(floatArrayOf(4.dp.toPx(), 3.dp.toPx())))
        drawCircle(color = color, radius = this.size.minDimension / 2f - 1.dp.toPx(), style = stroke)
        // head
        drawCircle(color = color, radius = this.size.minDimension * 0.14f, center = Offset(this.size.width / 2f, this.size.height * 0.38f))
        // shoulders
        drawArc(
            color = color,
            startAngle = 180f,
            sweepAngle = 180f,
            useCenter = true,
            topLeft = Offset(this.size.width * 0.27f, this.size.height * 0.56f),
            size = Size(this.size.width * 0.46f, this.size.height * 0.36f)
        )
    }
}

fun userInitials(name: String): String {
    val parts = name.trim().split(" ", "\t").filter { it.isNotBlank() }
    return when {
        parts.isEmpty() -> "?"
        parts.size == 1 -> parts[0].take(1).uppercase()
        else -> (parts.first().take(1) + parts.last().take(1)).uppercase()
    }
}

/** Avatar of a user: picture URL, else emoji, else initials. A null [user] renders the "unassigned" icon. */
@Composable
fun UserAvatar(user: UserDto?, size: Dp = 24.dp, modifier: Modifier = Modifier) {
    if (user == null) {
        UnassignedIcon(size = size, modifier = modifier)
        return
    }
    val initialsFallback: @Composable () -> Unit = {
        val emoji = user.avatarEmoji
        if (!emoji.isNullOrBlank()) {
            EmojiGlyph(emoji, size = size * 0.62f)
        } else {
            Text(
                userInitials(user.name),
                fontSize = (size.value * 0.42f).sp,
                fontWeight = FontWeight.Bold,
                color = Color.White
            )
        }
    }
    Box(
        modifier = modifier
            .size(size)
            .clip(CircleShape)
            .background(if (user.avatarEmoji.isNullOrBlank()) NibblerColors.BauhausBlue else NibblerColors.DarkSurfaceContainerHighest)
            .border(1.dp, NibblerColors.DarkOutlineThin, CircleShape),
        contentAlignment = Alignment.Center
    ) {
        RemoteImage(url = user.avatarUrl, modifier = Modifier.size(size), fallback = initialsFallback)
    }
}

/** Overlapping avatars of the people on a task: up to [maxShown], then a "+N" bubble; the unassigned icon when empty. */
@Composable
fun AssigneeAvatarStack(users: List<UserDto>, size: Dp = 24.dp, maxShown: Int = 3, modifier: Modifier = Modifier) {
    if (users.isEmpty()) {
        UnassignedIcon(size = size, modifier = modifier)
        return
    }
    val shown = users.take(maxShown)
    val extra = users.size - shown.size
    val step = size * 0.72f // each avatar covers about a quarter of the previous one
    val count = shown.size + if (extra > 0) 1 else 0
    Box(modifier = modifier.width(size + step * (count - 1)).height(size)) {
        shown.forEachIndexed { index, user ->
            UserAvatar(
                user,
                size,
                Modifier.offset(x = step * index).border(1.5.dp, NibblerColors.DarkSurfaceContainer, CircleShape)
            )
        }
        if (extra > 0) {
            Box(
                modifier = Modifier
                    .offset(x = step * shown.size)
                    .size(size)
                    .clip(CircleShape)
                    .background(NibblerColors.DarkSurfaceContainerHighest)
                    .border(1.5.dp, NibblerColors.DarkSurfaceContainer, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Text("+$extra", fontSize = (size.value * 0.38f).sp, fontWeight = FontWeight.Bold, color = NibblerColors.DarkOnSurface)
            }
        }
    }
}

/** A user's avatar followed by their name, used in pickers and the activity log. */
@Composable
fun UserChipContent(user: UserDto?, textColor: Color, avatarSize: Dp = 20.dp) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        UserAvatar(user, avatarSize)
        Text(
            text = user?.name ?: "Unassigned",
            modifier = Modifier.padding(start = 6.dp),
            fontSize = 12.sp,
            color = textColor
        )
    }
}

/** Square project picture, falling back to the project key on an indigo tile. */
@Composable
fun ProjectImage(project: ProjectDto, size: Dp = 40.dp, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .size(size)
            .clip(RoundedCornerShape(8.dp))
            .background(NibblerColors.BauhausBlue)
            .border(1.dp, NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp)),
        contentAlignment = Alignment.Center
    ) {
        RemoteImage(
            url = project.imageUrl,
            modifier = Modifier.size(size),
            fallback = {
                Text(
                    project.key.take(2).uppercase(),
                    fontSize = (size.value * 0.36f).sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
            }
        )
    }
}

private val MonthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

/** Turns the server's ISO timestamp (UTC) into e.g. "29 Sep 2026, 21:28 UTC". Unparseable input is returned as is. */
fun formatTimestamp(iso: String): String {
    if (iso.length < 16 || iso[10] != 'T') return iso
    val year = iso.substring(0, 4)
    val month = iso.substring(5, 7).toIntOrNull()?.let { MonthNames.getOrNull(it - 1) } ?: return iso
    val day = iso.substring(8, 10).trimStart('0').ifEmpty { "0" }
    return "$day $month $year, ${iso.substring(11, 16)} UTC"
}

/** "Oct 3" from the server's ISO timestamp; unparseable input is returned as is. */
fun formatShortDate(iso: String): String {
    if (iso.length < 10 || iso[4] != '-') return iso
    val month = iso.substring(5, 7).toIntOrNull()?.let { MonthNames.getOrNull(it - 1) } ?: return iso
    return "$month ${iso.substring(8, 10).trimStart('0').ifEmpty { "0" }}"
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun UserAvatarPreview() {
    Row(modifier = Modifier.background(NibblerColors.Background).padding(16.dp)) {
        val user = UserDto(id = "1", email = "a@b.c", name = "Test User", role = com.speedsoftware.nibbler.models.UserRole.USER, createdAt = "")
        UserAvatar(user, 32.dp)
        UserAvatar(user.copy(avatarEmoji = "X"), 32.dp, Modifier.padding(start = 8.dp))
        UserAvatar(null, 32.dp, Modifier.padding(start = 8.dp))
    }
}
